import { DocumentNode, gql } from '@apollo/client';
import { Box, BoxProps, Image } from '@chakra-ui/react';
import * as React from 'react';

import { ImagePreview } from '@/components/ImagePreview';
import { ImageFallback } from '@/imports/ui/chakra/components/ImageFallback';

import { OrganizationProfileImage_OrganizationFragment } from './graphql-types/OrganizationProfileImage_OrganizationFragment';

const ORGANIZATION_FRAGMENT = gql`
  fragment OrganizationProfileImage_OrganizationFragment on Organization {
    _id
    profilePicture {
      _id
      imageProxy(
        input: {
          w: 288
          h: 288
          fit: "fill"
          fill: "solid"
          fillColor: "white"
        }
      )
    }
  }
`;

interface OrganizationProfileImageWithOrganizationFragment<T>
  extends React.ForwardRefExoticComponent<T> {
  organizationFragment: DocumentNode;
}

type OrganizationProfileImageProps = {
  organization: OrganizationProfileImage_OrganizationFragment;
  preview?: boolean;
} & BoxProps;

export const OrganizationProfileImage = React.forwardRef<
  HTMLDivElement,
  OrganizationProfileImageProps
>(({ organization, children, preview, ...boxProps }, ref) => {
  const profilePictureSrc = organization.profilePicture?.imageProxy || undefined;
  const image = profilePictureSrc ? (
    <Image
      src={profilePictureSrc}
      alt="organization"
      w="full"
      h="full"
      objectFit="contain"
    />
  ) : (
    <ImageFallback aspectRatio="1" borderRadius="lg" bg="gray.200" />
  );

  return (
    <Box
      boxSize={36}
      p="2"
      boxShadow="base"
      borderRadius="lg"
      position="relative"
      bg="white"
      flexShrink="0"
      {...boxProps}
      ref={ref}
    >
      {preview && profilePictureSrc ? (
        <ImagePreview
          src={profilePictureSrc}
          alt="organization"
          w="full"
          h="full"
        >
          {image}
        </ImagePreview>
      ) : (
        image
      )}
      {children}
    </Box>
  );
}) as OrganizationProfileImageWithOrganizationFragment<
  React.PropsWithoutRef<OrganizationProfileImageProps>
> &
  React.RefAttributes<HTMLDivElement>;

OrganizationProfileImage.displayName = 'OrganizationProfileImage';

OrganizationProfileImage.organizationFragment = ORGANIZATION_FRAGMENT;
